import { LS_CURRENCY_FILTER_LAST_KNOWN } from 'constants/localStorageKeys'

// sessionStorage: scoped to this browser tab, survives reload, never shared with other tabs.
const SESSION_KEY = 'currencyFilterActive'

// Sentinel stored on disk for "All Currencies", so it can be told apart from "nothing stored yet".
const ALL_CURRENCIES_TOKEN = 'ALL'

type ActiveCurrencyFilterEntry = {
  userId: string
  value: string
}

type LastKnownCurrencyFilterEntry = {
  userId: string
  value: string
}

function readActiveEntry(): ActiveCurrencyFilterEntry | undefined {
  const raw = sessionStorage.getItem(SESSION_KEY)
  if (!raw) return undefined
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

function isLastKnownCurrencyFilterEntry(item: unknown): item is LastKnownCurrencyFilterEntry {
  const entry = item as Partial<LastKnownCurrencyFilterEntry> | null
  return typeof entry === 'object' && entry !== null && typeof entry.userId === 'string' && typeof entry.value === 'string'
}

function readLastKnownEntries(): LastKnownCurrencyFilterEntry[] {
  const raw = localStorage.getItem(LS_CURRENCY_FILTER_LAST_KNOWN)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isLastKnownCurrencyFilterEntry)
  } catch {
    return []
  }
}

/** True when this tab already holds an active selection that belongs to the given user. */
export function hasActiveCurrencyFilterForUser(userId: string): boolean {
  return readActiveEntry()?.userId === userId
}

/** Reads this tab's active currency filter for the given user. Returns undefined for "All Currencies". */
export function loadActiveCurrencyFilter(userId: string): string | undefined {
  const entry = readActiveEntry()
  if (!entry || entry.userId !== userId) return undefined
  return entry.value === ALL_CURRENCIES_TOKEN ? undefined : entry.value
}

/**
 * This tab's active currency filter value for display formatting, without the per-user guard used
 * for API headers. Returns undefined for "All Currencies" / no selection. A stale value in the brief
 * window right after an account switch is a cosmetic glitch that self-heals on the next render — it
 * never leaves the client, so the per-user check that matters for request headers isn't needed here.
 */
export function loadActiveCurrencyFilterValueForDisplay(): string | undefined {
  const entry = readActiveEntry()
  if (!entry || entry.value === ALL_CURRENCIES_TOKEN) return undefined
  return entry.value
}

/** Persists this tab's active currency filter for the given user (sessionStorage: per-tab, per-user). */
export function saveActiveCurrencyFilter(userId: string, value: string | undefined): void {
  const entry: ActiveCurrencyFilterEntry = { userId, value: value || ALL_CURRENCIES_TOKEN }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(entry))
}

/** Reads the last known currency filter for the given user across sessions/tabs on this browser. */
export function loadLastKnownCurrencyFilter(userId: string): string | undefined {
  const entry = readLastKnownEntries().find((item) => item.userId === userId)
  if (!entry) return undefined
  return entry.value === ALL_CURRENCIES_TOKEN ? undefined : entry.value
}

/** Persists the last known currency filter for the given user (localStorage: survives logout/reload). */
export function saveLastKnownCurrencyFilter(userId: string, value: string | undefined): void {
  const entries = readLastKnownEntries().filter((item) => item.userId !== userId)
  entries.push({ userId, value: value || ALL_CURRENCIES_TOKEN })
  localStorage.setItem(LS_CURRENCY_FILTER_LAST_KNOWN, JSON.stringify(entries))
}

/**
 * Reads the value to send as the `currency` request header for the current tab.
 * Scoped to the authenticated user's id so a stale entry left behind by a previous account on
 * this same tab (e.g. logout followed by a different login, before storage is reconciled) is
 * never forwarded on that account's requests.
 */
export function loadCurrentTabCurrencyHeaderValue(userId: string): string | undefined {
  const entry = readActiveEntry()
  if (!entry || entry.userId !== userId || entry.value === ALL_CURRENCIES_TOKEN) return undefined
  return entry.value
}
